import * as Sentry from '@sentry/browser'

import { version } from '../../package.json'

export const setupSentry = (token = '', environment = '') => {
  if (!token) {
    throw new Error('Token is required')
  }

  Sentry.init({
    dsn: token,
    environment,
    release: version,
    
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    // IDB connections drop after long idle sessions or SW update cycles.
    // redux-persist's writeFailHandler already handles this gracefully — no
    // user impact, no white screen. Filter here because Sentry's listener
    // is registered before our unhandledrejection handler.
    beforeSend(event, hint) {
      const err = hint?.originalException
      if (
        err &&
        err.name === 'UnknownError' &&
        String(err.message).includes('Indexed Database')
      ) {
        return null
      }
      // Browser extension noise: Safari/Chrome extensions calling runtime.sendMessage()
      // after their tab context has been lost. Not caused by app code.
      if (
        err &&
        String(err.message).includes('runtime.sendMessage') &&
        String(err.message).includes('Tab not found')
      ) {
        return null
      }
      // Chunk-load failures after a new deployment: the browser tries to
      // lazy-load a hashed chunk that no longer exists and receives an HTML
      // 404 page. Firefox/Chrome surface this as "error loading dynamically
      // imported module"; Safari surfaces it as "'text/html' is not a valid
      // JavaScript MIME type". Both are deployment artifacts — the
      // ErrorBoundary handles the reload, nothing to report to Sentry.
      if (err) {
        const msg = String(err.message)
        if (
          msg.includes('error loading dynamically imported module') ||
          msg.includes('is not a valid JavaScript MIME type')
        ) {
          return null
        }
      }
      return event
    },

    // remove integrations as this costs a lot of performance and we don't use it
    // integrations: [
    //   Sentry.replayIntegration()
    // ],
    // replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

}

export const identifyUser = (token = '', email = '') => {
  if (!token) {
    throw new Error('Token is required')
  }

  try {
    Sentry.configureScope((scope) => {
      scope.setUser({ username: email })
    })
  } catch (e) {
    console.error('Sentry identify error: ', e)
  }
}

export const logException = (err, msg) => {
  try {
    Sentry.captureMessage(msg)
    err && Sentry.captureException(err, { extra: msg })
  } catch (e) {
    console.error('Sentry error: ', e)
    console.log(msg)
    console.log(err)
  }
}

export const setupIndexedDBErrorHandler = () => {
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason &&
      event.reason.name === 'UnknownError' &&
      String(event.reason.message).includes('Indexed Database')
    ) {
      // IDB connection drops (e.g. during SW updates) are handled gracefully by
      // redux-persist's writeFailHandler. Token lives in localStorage so the user
      // stays logged in. No user action needed — suppress the unhandled rejection
      // so it doesn't surface as a Sentry exception.
      event.preventDefault();
    }
  });
};
